import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  NewReleases,
  Add,
  BugReport,
  Build,
  Security
} from '@mui/icons-material';

const ReleaseNotes = () => {
  const releases = [
    {
      version: '1.2.0',
      date: 'September 18, 2026',
      type: 'Feature Release',
      changes: [
        {
          type: 'feature',
          title: 'Diagram Navigation',
          description: 'Diagrams now fit to the screen automatically. Zoom centres on your cursor, drag to pan, and press F for fullscreen. Keyboard shortcuts for zoom, fit and panning are listed in the toolbar.'
        },
        {
          type: 'feature',
          title: 'Find a Node',
          description: 'Search the rendered diagram by name with Ctrl/Cmd+F. Matches are highlighted and Enter steps through them, centring each one - useful when a diagram has hundreds of applications.'
        },
        {
          type: 'feature',
          title: 'Minimap',
          description: 'An overview of the whole diagram in the corner, with a rectangle showing where you are. Click or drag it to jump around. Toggle with M.'
        },
        {
          type: 'feature',
          title: 'Collapsible Options Panel',
          description: 'Hide the configuration column to give the diagram the full width of the window. The choice is remembered, and a summary stays visible while collapsed.'
        },
        {
          type: 'feature',
          title: 'Large Diagram Warning',
          description: 'Diagrams too large to draw safely now warn you first instead of freezing the browser tab, with the option to render anyway.'
        },
        {
          type: 'improvement',
          title: 'Faster Diagram Generation',
          description: 'Generation is around 2.7x faster - measured 24.6s to 9.2s on a 25 application tenant. Group and filter names are now looked up in batches rather than one request at a time, and connections are reused.'
        },
        {
          type: 'improvement',
          title: 'Smaller Diagrams',
          description: 'Application icons are encoded more efficiently, cutting roughly 40% from diagram size. Icons are no longer fetched at all when Display Icons is turned off.'
        },
        {
          type: 'improvement',
          title: 'Full Height Workspace',
          description: 'The generator pages fill the window instead of scrolling, so the diagram gets the whole viewport. The options panel is more compact and its Generate button stays in view.'
        },
        {
          type: 'improvement',
          title: 'Responsive Under Load',
          description: 'Listing applications no longer blocks the server for everyone else while it runs.'
        },
        {
          type: 'bugfix',
          title: 'Block Selection Now Honoured',
          description: 'Choosing a block of applications sometimes rendered the entire tenant instead, with no warning. Selections are now filtered on the server and are far more reliable.'
        },
        {
          type: 'bugfix',
          title: 'Previous Diagram Left On Screen',
          description: 'Generating a second diagram could leave the first one displayed underneath, making a stale result look like the current one.'
        },
        {
          type: 'bugfix',
          title: 'Block Dialog Fixes',
          description: 'The large dataset dialog no longer reopens and discards your choice after selecting a block, and its buttons now describe what they actually do.'
        }
      ]
    },
    {
      version: '1.1.0',
      date: 'February 12, 2026',
      type: 'Feature Release',
      changes: [
        {
          type: 'feature',
          title: 'OS Prefix for All Profile Types',
          description: 'Profile Types dropdown now shows OS prefixes (Windows, iOS, macOS, Linux) for clearer identification'
        },
        {
          type: 'feature',
          title: 'Dynamic Profile Types Filtering',
          description: 'Profile Types dropdown dynamically filters based on selected Operating Systems'
        },
        {
          type: 'feature',
          title: 'Scope Tag Filtering for Profile Selection',
          description: 'Profile Types dropdown and selection list now filter to show only profiles within selected scope tags'
        }
      ]
    },
    {
      version: '1.0.1',
      date: 'February 3, 2026',
      type: 'Feature Release',
      changes: [
        {
          type: 'feature',
          title: 'Scope Tag Filtering',
          description: 'Filter applications and configuration profiles by scope tags in both Online and Offline modes'
        },
        {
          type: 'feature',
          title: 'Scope Tag Export',
          description: 'Export scripts now include roleScopeTagIds and roleScopeTagNames in the JSON output'
        },
        {
          type: 'improvement',
          title: 'Reset Filters on Tab Switch',
          description: 'All filter selections now reset to defaults when switching between Applications and Profiles'
        },
        {
          type: 'improvement',
          title: 'New RBAC Permission',
          description: 'Added DeviceManagementRBAC.Read.All permission to read scope tags from Intune'
        }
      ]
    },
    {
      version: '1.0.0',
      date: 'January 2026',
      type: 'Initial Release',
      changes: [
        {
          type: 'feature',
          title: 'Online Mode',
          description: 'Generate Mermaid diagrams directly from your Intune tenant with live data'
        },
        {
          type: 'feature',
          title: 'Offline Mode',
          description: 'Upload exported JSON files to generate diagrams without authentication'
        },
        {
          type: 'feature',
          title: 'Application Diagrams',
          description: 'Visualize Intune applications grouped by name or assignments'
        },
        {
          type: 'feature',
          title: 'Configuration Profile Diagrams',
          description: 'Visualize device configuration profiles and settings catalog policies'
        },
        {
          type: 'feature',
          title: 'Multiple Export Formats',
          description: 'Export diagrams as PNG, SVG, PDF, or Mermaid code'
        },
        {
          type: 'feature',
          title: 'ELK Layout Engine',
          description: 'Optional advanced layout algorithm for complex diagrams'
        }
      ]
    }
  ];

  const getChangeIcon = (type) => {
    switch (type) {
      case 'feature':
        return <Add color="success" />;
      case 'bugfix':
        return <BugReport color="error" />;
      case 'improvement':
        return <Build color="primary" />;
      case 'security':
        return <Security color="warning" />;
      default:
        return <NewReleases color="info" />;
    }
  };

  const getChangeColor = (type) => {
    switch (type) {
      case 'feature':
        return 'success';
      case 'bugfix':
        return 'error';
      case 'improvement':
        return 'primary';
      case 'security':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Release Notes
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          What's new in IntuneMermaid
        </Typography>
      </Box>

      {/* Releases */}
      {releases.map((release, index) => (
        <Card key={release.version} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                  v{release.version}
                </Typography>
                <Chip 
                  label={release.type} 
                  size="small" 
                  color={index === 0 ? 'primary' : 'default'}
                  variant={index === 0 ? 'filled' : 'outlined'}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {release.date}
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <List dense>
              {release.changes.map((change, changeIndex) => (
                <ListItem key={changeIndex} sx={{ alignItems: 'flex-start', py: 1 }}>
                  <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                    {getChangeIcon(change.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {change.title}
                        </Typography>
                        <Chip 
                          label={change.type} 
                          size="small" 
                          color={getChangeColor(change.type)}
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      </Box>
                    }
                    secondary={change.description}
                    secondaryTypographyProps={{
                      sx: { mt: 0.5 }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      ))}
    </Container>
  );
};

export default ReleaseNotes;
